import { CircleOff, CirclePlay, FileText, Info, Pencil, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
import { PATHS } from '@/app/router/routes'
import { Permission } from '@/features/roles/consts/permissions'
import { DetailFieldRow } from '@/shared/components/detail-rows/detail-field-row'
import { UserChip } from '@/shared/components/detail-rows/user-chip'
import { SidePanelActions } from '@/shared/components/side-panel/side-panel-actions'
import {
  SidePanelSection,
  SidePanelSectionBody
} from '@/shared/components/side-panel/side-panel-blocks'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { useSidePanelStore } from '@/shared/store/side-panel.store'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/shared/ui/accordion'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/shared/ui/alert-dialog'
import { Skeleton } from '@/shared/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs'
import { formatHumanDateTime } from '@/shared/utils/format-date'
import { getErrorMessage } from '@/shared/utils/handle-api-error'
import { useDeleteStateV2Mutation, useUpdateStateV2Mutation } from '../hooks/use-state-v2-mutations'
import { useStateV2AttachmentRequirementsQuery } from '../hooks/use-states-admin-v2-query'
import { LABEL_TYPE_NOTIFICATION } from '../lib/consts'
import type { StateV2AdminRow } from '../models/state-admin-v2'
import {
  formatStateSla,
  StateActiveBadge,
  StateColorDot,
  StateTypeBadge
} from './states-admin-columns'

type StateV2DetailPanelProps = {
  state: StateV2AdminRow
}

/**
 * Panel de detalle del estado contra la API v2. Espeja el del v1 —resumen,
 * acciones, acordeones de notificación/requisitos/actividad— y sólo cambia de
 * dónde salen los datos:
 *
 * - Se identifica por `sta_`, no por `_id`.
 * - Los requisitos documentales YA NO vienen en la fila: son un subrecurso y se
 *   piden aparte. El acordeón muestra el conteo de la fila mientras carga, así
 *   que el encabezado nunca parpadea de «(0)» al número real.
 *
 * La EDICIÓN sigue viviendo en la página completa porque incluye el editor de
 * requisitos del portal. Acciones que el backend rechaza de plano se OCULTAN:
 * Eliminar y Editar para filas de sistema (blocked).
 */
export function StateV2DetailPanel({ state }: StateV2DetailPanelProps) {
  const [confirmDelete, setConfirmDelete] = useState(false)
  const closePanel = useSidePanelStore((s) => s.closePanel)
  const navigate = useNavigate()
  const updateMutation = useUpdateStateV2Mutation()
  const deleteMutation = useDeleteStateV2Mutation()
  const { has } = usePermissions()
  const requirementsQuery = useStateV2AttachmentRequirementsQuery(state.id)

  // Las filas de sistema (blocked) no se editan ni se eliminan.
  const isSystemRow = state.blocked

  // Las mutaciones v2 no avisan por sí solas (las encadena el editor), así que
  // el aviso lo pone el consumidor.
  const toggleActive = () => {
    updateMutation.mutate(
      { id: state.id, body: { active: !state.active } },
      {
        onSuccess: () => {
          toast.success(state.active ? 'Estado desactivado' : 'Estado activado')
        },
        onError: (error) => {
          toast.error(getErrorMessage(error))
        }
      }
    )
  }

  const requirements = requirementsQuery.data ?? []
  const customEmails = state.emailsNotification
  const notificationLabels = state.notification.map(
    (recipient) => LABEL_TYPE_NOTIFICATION[recipient] ?? recipient
  )

  return (
    <div className="flex flex-col">
      <SidePanelSection>
        <SidePanelSectionBody className="gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <StateColorDot color={state.color} />
            <h3 className="truncate text-base font-semibold leading-tight">{state.name}</h3>
          </div>
          {state.description && (
            <p className="text-sm text-muted-foreground">{state.description}</p>
          )}
          <div className="flex flex-col gap-2">
            <DetailFieldRow label="Estado" value={<StateActiveBadge active={state.active} />} />
            <DetailFieldRow label="Tipo" value={<StateTypeBadge type={state.type} />} />
            <DetailFieldRow label="SLA" value={formatStateSla(state.time, state.interval)} />
            <DetailFieldRow label="Visible" value={state.visible ? 'Sí' : 'No'} />
            <DetailFieldRow label="Envía a SAP" value={state.sendSAP ? 'Sí' : 'No'} />
          </div>
        </SidePanelSectionBody>
      </SidePanelSection>

      <SidePanelSection>
        <SidePanelActions
          maxInline={2}
          actions={[
            ...(has(Permission.UpdateStates) && !isSystemRow
              ? [
                  {
                    label: 'Editar estado',
                    icon: <Pencil className="h-4 w-4" />,
                    variant: 'default' as const,
                    primary: true,
                    onClick: () => {
                      closePanel()
                      void navigate(`${PATHS.admin.business.states}/${state.id}`)
                    }
                  }
                ]
              : []),
            ...(has(Permission.UpdateStates)
              ? [
                  {
                    label: state.active ? 'Desactivar estado' : 'Activar estado',
                    icon: state.active ? (
                      <CircleOff className="h-4 w-4" />
                    ) : (
                      <CirclePlay className="h-4 w-4" />
                    ),
                    variant: 'outline' as const,
                    disabled: updateMutation.isPending,
                    onClick: toggleActive
                  }
                ]
              : []),
            ...(has(Permission.DeleteStates) && !isSystemRow
              ? [
                  {
                    label: 'Eliminar estado',
                    icon: <Trash2 className="h-4 w-4" />,
                    variant: 'destructive' as const,
                    disabled: deleteMutation.isPending,
                    onClick: () => {
                      setConfirmDelete(true)
                    }
                  }
                ]
              : [])
          ]}
        />
      </SidePanelSection>

      <Tabs defaultValue="more-info">
        <TabsList className="w-full justify-normal rounded-none">
          <TabsTrigger className="flex gap-1" value="more-info">
            <Info className="size-4" /> Más información
          </TabsTrigger>
        </TabsList>
        <TabsContent value="more-info">
          <Accordion type="multiple" defaultValue={['notification', 'activity']}>
            <AccordionItem value="notification">
              <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border">
                Notificaciones
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2 pt-2 pl-3 pr-2">
                  <DetailFieldRow
                    label="Destinatarios"
                    value={notificationLabels.length ? notificationLabels.join(', ') : '—'}
                  />
                  <DetailFieldRow
                    label="Correos personalizados"
                    value={customEmails.length ? customEmails.join(', ') : '—'}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="requirements">
              <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border">
                <span className="flex items-center gap-2">
                  <FileText className="size-4" />
                  {/* El conteo sale de la fila: ya lo sabemos antes de pedir la lista. */}
                  Requisitos documentales ({state.attachmentRequirementsCount})
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2 pt-2 pl-3 pr-2">
                  {requirementsQuery.isLoading && (
                    <>
                      <Skeleton className="h-4 w-full rounded-sm" />
                      <Skeleton className="h-4 w-2/3 rounded-sm" />
                    </>
                  )}
                  {requirementsQuery.isError && (
                    <p className="text-sm text-muted-foreground">
                      No se pudieron cargar los requisitos.
                    </p>
                  )}
                  {!requirementsQuery.isLoading &&
                    !requirementsQuery.isError &&
                    requirements.length === 0 && (
                      <p className="text-sm text-muted-foreground">
                        Este estado no exige documentos.
                      </p>
                    )}
                  {requirements.map((requirement) => (
                    <DetailFieldRow
                      key={requirement.id}
                      label={requirement.name}
                      value={requirement.isRequired ? 'Obligatorio' : 'Opcional'}
                    />
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="activity">
              <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border">
                Actividad
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2 pt-2 pl-3 pr-2">
                  <DetailFieldRow label="Creado" value={formatHumanDateTime(state.createdAt)} />
                  <DetailFieldRow
                    label="Creado por"
                    value={<UserChip name={state.createdBy?.fullname} />}
                  />
                  <DetailFieldRow
                    label="Actualizado"
                    value={formatHumanDateTime(state.updatedAt)}
                  />
                  <DetailFieldRow
                    label="Actualizado por"
                    value={<UserChip name={state.updatedBy?.fullname} />}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </TabsContent>
      </Tabs>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar estado</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará “{state.name}” y se quitará de todos los flujos de trabajo que lo
              incluyen. Los clientes y negociaciones que están en este estado conservan su
              referencia histórica.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                deleteMutation.mutate(state.id, {
                  onSuccess: () => {
                    closePanel()
                  }
                })
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
